import React, { useEffect, useRef, useState } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { TenantProvider } from "@/context/TenantContext";
import TenantAdmin from "@/pages/TenantAdmin";
import AgencySignup from "@/pages/AgencySignup";
import TenantsManager from "@/pages/TenantsManager";
import { CartProvider } from "@/context/CartContext";
import Home from "@/pages/Home";
import FlightResults from "@/pages/FlightResults";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Checkout from "@/pages/Checkout";
import HolidaysPage from "@/pages/HolidaysPage";
import CartPage from "@/pages/CartPage";
import CartCheckout from "@/pages/CartCheckout";
import LegalPage from "@/pages/LegalPages";
import ContactPage from "@/pages/ContactPage";
import PackageDetail from "@/pages/PackageDetail";
import {
  HotelPage,
  PackagePage,
  BusPage,
  VisaPage,
  GroupEnquiryPage,
  SimpleEnquiryPage,
} from "@/pages/StaticPages";
import { Car, TrainFront, MonitorCheck, Plane, Headset } from "lucide-react";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Handles Google OAuth redirect: {origin}/dashboard#session_id=xxx
const AuthCallback = () => {
  const { completeGoogle } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    if (!sid) {
      navigate("/login");
      return;
    }
    completeGoogle(sid).then((res) => {
      // Clear the hash then route to dashboard (or pending checkout)
      window.history.replaceState(null, "", window.location.pathname);
      const pending = sessionStorage.getItem("ft_checkout");
      navigate(res.ok ? (pending ? "/checkout" : "/dashboard") : "/login");
    });
  }, [completeGoogle, navigate]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#F5F7FB]">
      <Plane className="h-10 w-10 animate-bounce text-[#1B1F5B]" />
      <p className="mt-4 text-sm font-semibold text-slate-500">
        Signing you in with Google...
      </p>
    </div>
  );
};

function AppRouter() {
  const location = useLocation();
  // Detect session_id synchronously during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/flight/search" element={<FlightResults />} />
        <Route path="/login" element={<Login />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/cart-checkout" element={<CartCheckout />} />
        <Route path="/hotel-page" element={<HotelPage />} />
        <Route path="/package" element={<HolidaysPage />} />
        <Route path="/package-details/:slug" element={<PackageDetail />} />
        <Route path="/package-details/:slug/:id" element={<PackageDetail />} />
        <Route path="/holidays" element={<HolidaysPage />} />
        <Route path="/bus" element={<BusPage />} />
        <Route path="/visa" element={<VisaPage />} />
        <Route path="/group-enquiry" element={<GroupEnquiryPage />} />
        <Route path="/about" element={<LegalPage page="about" />} />
        <Route path="/terms" element={<LegalPage page="terms" />} />
        <Route path="/privacy" element={<LegalPage page="privacy" />} />
        <Route path="/payment-security" element={<LegalPage page="payment-security" />} />
        <Route path="/cancellation" element={<LegalPage page="cancellation" />} />
        <Route path="/refund" element={<LegalPage page="refund" />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/agency-admin" element={<TenantAdmin />} />
        <Route path="/agency-signup" element={<AgencySignup />} />
        <Route path="/admin/tenants" element={<TenantsManager />} />
        <Route
          path="/cab-enquiry"
          element={
            <SimpleEnquiryPage
              title="Cab Enquiry"
              subtitle="Comfortable and trusted rides, airport transfers & outstation cabs"
              icon={Car}
              enquiryType="cab"
            />
          }
        />
        <Route
          path="/train-enquiry"
          element={
            <SimpleEnquiryPage
              title="Train Enquiry"
              subtitle="Train bookings & PNR assistance across India"
              icon={TrainFront}
              enquiryType="train"
            />
          }
        />
        <Route
          path="/web-checkin"
          element={
            <SimpleEnquiryPage
              title="Web Check-in"
              subtitle="Check-in online and get your boarding pass instantly"
              icon={MonitorCheck}
              enquiryType="webcheckin"
            />
          }
        />
        <Route
          path="/contact"
          element={
            <SimpleEnquiryPage
              title="Contact Us"
              subtitle="Have a question? Our travel experts are here to help you 24x7"
              icon={Headset}
              enquiryType="contact"
            />
          }
        />
        <Route path="*" element={<Home />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <TenantProvider>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Toaster position="top-center" richColors />
            <AppRouter />
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
      </TenantProvider>
    </div>
  );
}

export default App;
